import { formatDateOnly } from "../dates";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { alphaSave } from "../ui/alpha-save";
import { ErrorState, Loading } from "../ui/State";
type Obligation = {
  id: string;
  title: string;
  description: string;
  obligation_type: string;
  next_due_date: string;
  version: number;
  status: string;
  action_id: string | null;
};
type Model = {
  items: Obligation[];
  organisations: { id: string; legal_name: string }[];
  documents: { id: string; versionId: string; title: string }[];
  history: {
    id: string;
    obligation_id: string;
    completed_on: string;
    review_note: string;
  }[];
};
export function CompanyAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [data, setData] = useState<Model>(),
    [selected, setSelected] = useState(""),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<{ url: string; body: object } | null>(null);
  const load = () =>
    api<Model>("/api/alpha/company?building=" + building).then(setData);
  useEffect(() => {
    setData(undefined);
    setSelected("");
    if (building) void load().catch(setError);
  }, [building]);
  async function write(url: string, body: object) {
    setBusy(true);
    setError(undefined);
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    try {
      await alphaSave(pending, pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void write("/api/alpha/company", {
      building,
      organisationId: f.get("company"),
      kind: f.get("kind"),
      title: f.get("title"),
      description: f.get("description"),
      dueOn: f.get("due"),
      cadenceMonths: Number(f.get("cadence")),
    });
  }
  const row = data?.items.find((i) => i.id === selected);
  function complete(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget),
      d = data!.documents.find((d) => d.id === f.get("evidence"))!;
    void write("/api/alpha/company/" + selected + "/complete", {
      expectedVersion: row!.version,
      completedOn: f.get("completed"),
      nextDueOn: f.get("next") || null,
      documentId: d.id,
      versionId: d.versionId,
      reviewNote: f.get("note"),
    });
  }
  return (
    <>
      <h1>Company administration</h1>
      <p>
        Prepare obligations, officer-change reviews and meeting follow-ups.
        Retain filing evidence after review. Officer appointments remain in
        Contacts; nothing is filed externally from this page.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void write("", {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Open company administration from a building.</p>
      ) : !data ? (
        <Loading />
      ) : (
        <>
          <form className="panel form" onSubmit={create}>
            <fieldset disabled={busy || !!pending.current}>
              <legend>Prepare obligation</legend>
              <label>
                Client company
                <select name="company" required>
                  <option value="">Choose company</option>
                  {data.organisations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.legal_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Type
                <select name="kind">
                  <option>Officer change</option>
                  <option>Confirmation statement</option>
                  <option>Accounts filing</option>
                  <option>Meeting follow-up</option>
                </select>
              </label>
              <label>
                Title
                <input name="title" required minLength={5} maxLength={240} />
              </label>
              <label>
                Change or obligation to review
                <textarea
                  name="description"
                  required
                  minLength={10}
                  maxLength={5000}
                />
              </label>
              <label>
                Due on
                <DateOnlyInput name="due" required />
              </label>
              <label>
                Review cadence in months
                <input
                  type="number"
                  name="cadence"
                  min={1}
                  max={120}
                  defaultValue={12}
                />
              </label>
              <button>Prepare review and reminder</button>
            </fieldset>
          </form>
          <section className="panel">
            <h2>Company obligations</h2>
            {data.items.length===0&&<p>No company obligations have been recorded for this building.</p>}
            {data.items.map((i) => (
              <p key={i.id}>
                <button
                  disabled={busy || !!pending.current}
                  onClick={() => setSelected(i.id)}
                >
                  {i.title}
                </button>{" "}
                · {i.status} · Due {formatDateOnly(i.next_due_date)}
              </p>
            ))}
          </section>
          {row && (
            <section className="panel form">
              <h2>{row.title}</h2>
              <p>{row.description}</p>
              {row.status === "Active" && !row.action_id && (
                <button
                  disabled={busy || !!pending.current}
                  onClick={() =>
                    void write("/api/alpha/company/" + row.id + "/remind", {
                      expectedVersion: row.version,
                    })
                  }
                >
                  Prepare next reminder
                </button>
              )}
              <form key={row.id + row.version} onSubmit={complete}>
                <fieldset
                  disabled={
                    busy || !!pending.current || row.status !== "Active"
                  }
                >
                  <legend>Review completion evidence</legend>
                  <label>
                    Completed on
                    <DateOnlyInput name="completed" required />
                  </label>
                  <label>
                    Reviewed next due date (leave blank for one-off completion)
                    <DateOnlyInput name="next" />
                  </label>
                  <label>
                    Verified filing or completion document
                    <select name="evidence" required>
                      <option value="">Choose evidence</option>
                      {data.documents.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Review note
                    <textarea
                      name="note"
                      required
                      minLength={10}
                      maxLength={5000}
                    />
                  </label>
                  <button>Retain reviewed completion</button>
                </fieldset>
              </form>
              <p>
                Any linked Action keeps its separate verification and closure
                requirements.
              </p>
              <h3>Retained completions</h3>
              {data.history
                .filter((h) => h.obligation_id === row.id)
                .map((h) => (
                  <p key={h.id}>
                    {formatDateOnly(h.completed_on)} · {h.review_note}
                  </p>
                ))}
            </section>
          )}
        </>
      )}
    </>
  );
}
